import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Backend } from './backend';
import { createLocalBackend, loadAwsBackend, usesAwsBackend } from './load-backend';

const BackendContext = createContext<Backend | undefined>(undefined);

/**
 * Provides the backend for the lifetime of the page (backend.ts): the one given (tests), the real
 * AWS one in the deployed build (loaded in the background, T074a), or the local one.
 */
export function BackendProvider({ backend, children }: { backend?: Backend; children: ReactNode }) {
  const [value, setValue] = useState<Backend | undefined>(
    () => backend ?? (usesAwsBackend() ? undefined : createLocalBackend()),
  );

  useEffect(() => {
    if (value) return;
    let cancelled = false;
    void loadAwsBackend().then((loaded) => {
      if (!cancelled) setValue(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [value]);

  // Until the AWS code has loaded, the sign-in page's background (no flash of a form).
  if (!value) return <div className="sign-in" aria-busy="true" />;
  return <BackendContext.Provider value={value}>{children}</BackendContext.Provider>;
}

export function useBackend(): Backend {
  const value = useContext(BackendContext);
  if (!value) {
    throw new Error('useBackend() must be used within a <BackendProvider>');
  }
  return value;
}
